/**
 * Prepares code pasted from an AI tool for a Genesis widget and explains what
 * happened. The result is saved with `scoper: 2`, so CSS is scoped by
 * scopeCss() and scripts wrapped by wrapWidgetScriptV2() when rendered.
 *
 * Nothing here runs the pasted code.
 */
import { parsePaste, PastedCode } from './parsePaste';
import { moveInlineHandlers } from './inlineHandlers';
import { scopeCss, ScopeCssReport } from './scopeCss';
import { applyBrandFixes, brandFindings, BrandFixKind } from './brandCheck';
import type { HouseStyle } from './houseStyle';

export const SCOPER_V2 = 2;
export const ASSET_HOSTS = ['genesis-cdn.ff.com', 'genesis-cdn.faradayfuturecn.com'];

export type FindingLevel = 'error' | 'warn' | 'info';

export type Finding = {
  id: string;
  level: FindingLevel;
  title: string;
  detail?: string;
  /** A line for the fix request sent back to the AI tool. */
  ask?: string;
  /** Items the finding is about (URLs, selectors…), for display. */
  items?: string[];
};

export type ImageRef = {
  /** As written in the code (a data: URI, or a URL). */
  src: string;
  kind: 'base64' | 'outside' | 'relative';
  /** Approximate size in bytes, for base64. */
  bytes?: number;
};

export type NormalizedWidgetCode = {
  html: string;
  css: string;
  js: string;
  scoper: typeof SCOPER_V2;
  /** What Genesis changed, in plain words. */
  fixes: string[];
  findings: Finding[];
  /** Text to paste back into the AI tool; empty when nothing needs asking. */
  fixRequest: string;
  images: ImageRef[];
  source: PastedCode['source'];
  cssReport: ScopeCssReport;
};

const plural = (n: number, one: string, many: string) => `${n} ${n === 1 ? one : many}`;
const unique = <T>(list: T[]) => Array.from(new Set(list));

const hostOf = (url: string): string => {
  const m = url.match(/^https?:\/\/([^/?#:]+)/i);
  return m ? m[1].toLowerCase() : '';
};

// Where "Move to Assets" puts images in this environment (an S3 bucket in dev and QA), from the
// browser settings, or on the server (the connector's checks) from its own; the production CDNs are
// listed above.
const environmentAssetHost = (): string => {
  try {
    if (typeof window === 'undefined') {
      const base = typeof process !== 'undefined' && process.env ? process.env.S3_ASSETS_MANAGEMENT : '';
      return base ? hostOf(base) : '';
    }
    const env = (window as unknown as { _env_?: Record<string, string> })._env_;
    return env && env.S3_ASSETS_MANAGEMENT ? hostOf(env.S3_ASSETS_MANAGEMENT) : '';
  } catch (e) {
    return '';
  }
};

const isAssetHost = (url: string) => {
  const host = hostOf(url);
  return ASSET_HOSTS.includes(host) || (!!host && host === environmentAssetHost());
};

/**
 * The URLs in a srcset. Candidates are split on commas that end a URL or follow its
 * descriptor, so URLs with commas inside (Cloudinary's w_400,h_300) stay whole.
 */
export const srcsetUrls = (value: string): string[] => {
  const urls: string[] = [];
  let i = 0;
  while (i < value.length) {
    while (i < value.length && /[\s,]/.test(value[i])) i++;
    const start = i;
    while (i < value.length && !/\s/.test(value[i])) i++;
    let url = value.slice(start, i);
    const endsCandidate = /,$/.test(url);
    url = url.replace(/,+$/, '');
    if (url) urls.push(url);
    if (!endsCandidate) while (i < value.length && value[i] !== ',') i++;
  }
  return urls;
};

/** Image references in HTML attributes, inline styles and CSS url(). */
export const findImages = (html: string, css: string): ImageRef[] => {
  const refs: string[] = [];
  let m: RegExpExecArray | null;
  // Every image-like attribute on image-like tags.
  const tagRe = /<(img|source|video|image|input)\b([^>]*)>/gi;
  while ((m = tagRe.exec(html))) {
    const attrs = m[2];
    const valueRe = /\s(src|srcset|poster|href|xlink:href|data-src|data-srcset)\s*=\s*(?:"([^"]*)"|'([^']*)')/gi;
    let a: RegExpExecArray | null;
    while ((a = valueRe.exec(attrs))) {
      const value = a[2] ?? a[3] ?? '';
      const isSet = /srcset/i.test(a[1]);
      if (isSet && !/^\s*data:/i.test(value)) srcsetUrls(value).forEach(url => refs.push(url));
      else refs.push(value.trim());
    }
  }
  const urlRe = /url\(\s*(?:"([^"]*)"|'([^']*)'|([^)\s]*))\s*\)/gi;
  // @import url(...) loads a stylesheet, not an image.
  const cssWithoutImports = css.replace(/@import\s[^;]*;?/gi, '');
  [html, cssWithoutImports].forEach((text) => {
    while ((m = urlRe.exec(text))) refs.push((m[1] ?? m[2] ?? m[3] ?? '').trim());
  });
  const out: ImageRef[] = [];
  const seen = new Set<string>();
  refs.forEach((src) => {
    if (!src || seen.has(src) || src.includes('{{') || src.startsWith('#')) return;
    seen.add(src);
    if (/^data:image\//i.test(src)) {
      const comma = src.indexOf(',');
      const isBase64 = /;base64,/i.test(src.slice(0, comma + 1));
      const bytes = isBase64 ? Math.round((src.length - comma - 1) * 0.75) : src.length - comma - 1;
      if (bytes > 1024) out.push({ src, kind: 'base64', bytes }); // tiny inline SVG icons are fine
      return;
    }
    if (/^data:/i.test(src) || /\.(woff2?|ttf|otf|eot|css)(\?|#|$)/i.test(src) || /^https?:\/\/fonts\.(googleapis|gstatic)\.com\//i.test(src)) return; // fonts, stylesheets, other data
    if (/^https?:\/\//i.test(src) || src.startsWith('//')) {
      if (!isAssetHost(src.startsWith('//') ? `https:${src}` : src)) out.push({ src, kind: 'outside' });
      return;
    }
    if (/^(blob:|about:|mailto:|tel:|javascript:)/i.test(src)) return;
    out.push({ src, kind: 'relative' });
  });
  return out;
};

const LIBRARIES: Array<[RegExp, string]> = [
  [/\bgsap\b|\bScrollTrigger\b/, 'GSAP'],
  [/\bnew Swiper\b/, 'Swiper'],
  [/\bnew Chart\b/, 'Chart.js'],
  [/\bTHREE\./, 'three.js'],
  [/(^|[^\w$.])(\$|jQuery)\(/, 'jQuery'],
  [/\bAOS\.init\b/, 'AOS'],
  [/\blottie\./, 'Lottie'],
  [/\banime\(/, 'anime.js'],
  [/\bAlpine\./, 'Alpine.js']
];

const scriptFindings = (js: string, parsed?: PastedCode): Finding[] => {
  const findings: Finding[] = [];
  const externalScripts = parsed ? parsed.externalScripts : [];
  const tailwind = externalScripts.some(s => /tailwind/i.test(s)) || Boolean(parsed && parsed.removed.tailwindConfig);
  const otherScripts = externalScripts.filter(s => !/tailwind/i.test(s));
  if (tailwind) {
    findings.push({
      id: 'tailwind',
      level: 'error',
      title: 'Uses Tailwind, which published pages don’t load',
      detail: 'The Tailwind classes in this HTML won’t style anything on ff.com.',
      ask: 'Don’t use Tailwind or any CDN. Write plain CSS in the css block.'
    });
  }
  const libs = unique(LIBRARIES.filter(([re]) => re.test(js)).map(([, name]) => name));
  if (otherScripts.length || libs.length) {
    findings.push({
      id: 'external-scripts',
      level: 'error',
      title: `Needs ${libs.length ? libs.join(', ') : 'outside scripts'}, which Genesis doesn’t load`,
      detail: 'The parts of the script that depend on them will fail.',
      items: otherScripts,
      ask: `Don’t use outside libraries${libs.length ? ` (${libs.join(', ')})` : ''} or script tags with src. Write plain JavaScript.`
    });
  }
  if (parsed && parsed.externalStyles.length) {
    findings.push({
      id: 'external-styles',
      level: 'warn',
      title: plural(parsed.externalStyles.length, 'outside stylesheet isn’t loaded', 'outside stylesheets aren’t loaded'),
      items: parsed.externalStyles,
      ask: 'Don’t link outside stylesheets; put all CSS in the css block.'
    });
  }
  if ((parsed && parsed.removed.moduleScripts) || /^\s*(import\s[\w{*]|import\s*['"]|export\s)/m.test(js)) {
    findings.push({
      id: 'modules',
      level: 'error',
      title: 'Uses JavaScript modules (import/export), which a widget script can’t run',
      ask: 'Don’t use import or export; write one plain script.'
    });
  }
  const network = [/\bfetch\s*\(/, /\bXMLHttpRequest\b/, /\bnew WebSocket\b/, /\bsendBeacon\b/, /\bnew EventSource\b/].some(re => re.test(js));
  if (network) {
    const urls = unique((js.match(/https?:\/\/[^\s'"`)]+/g) || []).map(hostOf).filter(Boolean));
    findings.push({
      id: 'network',
      level: 'warn',
      title: 'Contacts other servers',
      detail: 'Check that this is intended. The script runs with your Genesis session in the editor.',
      items: urls
    });
  }
  if (/\beval\s*\(|\bnew Function\s*\(|set(Timeout|Interval)\s*\(\s*['"`]/.test(js)) {
    findings.push({ id: 'eval', level: 'warn', title: 'Runs code from text (eval or new Function)', ask: 'Don’t use eval, new Function or string timers.' });
  }
  if (/\bdocument\.cookie\b/.test(js)) {
    findings.push({ id: 'cookie', level: 'warn', title: 'Reads or writes cookies', ask: 'Don’t use cookies.' });
  }
  if (/\bdocument\.write(ln)?\s*\(/.test(js)) {
    findings.push({ id: 'document-write', level: 'error', title: 'Uses document.write, which wipes a loaded page', ask: 'Don’t use document.write; change elements inside root instead.' });
  }
  return findings;
};

const cssFindings = (report: ScopeCssReport): Finding[] => {
  const findings: Finding[] = [];
  if (report.fixedPositionSelectors.length) {
    findings.push({
      id: 'position-fixed',
      level: 'warn',
      title: 'position: fixed pins part of this section over the whole page',
      items: report.fixedPositionSelectors,
      ask: 'Avoid position: fixed; this code is one section of a longer page. Use position: sticky or absolute inside the section.'
    });
  }
  if (report.remBaseChanges.length) {
    findings.push({
      id: 'rem-base',
      level: 'warn',
      title: 'Sets the page’s root font size, which a section can’t change',
      detail: `rem units use the site’s size instead (${report.remBaseChanges.join('; ')} isn’t applied).`,
      ask: 'Don’t set font-size on html or :root; use px or rem values that assume a 16px root.'
    });
  }
  const outsideImports = report.imports.filter(i => !/fonts\.googleapis\.com|fonts\.bunny\.net/i.test(i));
  if (outsideImports.length) {
    findings.push({ id: 'css-import', level: 'warn', title: 'Imports an outside stylesheet', items: outsideImports, ask: 'Don’t @import outside stylesheets (Google Fonts is fine).' });
  }
  return findings;
};

const imageFindings = (images: ImageRef[]): Finding[] => {
  const findings: Finding[] = [];
  const base64 = images.filter(i => i.kind === 'base64');
  const outside = images.filter(i => i.kind === 'outside');
  const relative = images.filter(i => i.kind === 'relative');
  if (base64.length) {
    const kb = Math.round(base64.reduce((sum, i) => sum + (i.bytes || 0), 0) / 1024);
    findings.push({
      id: 'base64-images',
      level: 'warn',
      title: `${plural(base64.length, 'image is', 'images are')} embedded in the code (${kb} KB)`,
      detail: `Embedded images make every page that uses this load slower. Move ${base64.length === 1 ? 'it' : 'them'} to Assets.`,
      ask: 'Don’t embed images as base64. Use the image URLs I give you.'
    });
  }
  if (outside.length) {
    findings.push({
      id: 'outside-images',
      level: 'warn',
      title: `${plural(outside.length, 'image is', 'images are')} hosted outside Genesis`,
      detail: `${outside.length === 1 ? 'It' : 'They'} can disappear or change without notice. Move ${outside.length === 1 ? 'it' : 'them'} to Assets.`,
      items: outside.map(i => i.src),
      ask: 'Only use image URLs on genesis-cdn.ff.com that I give you; no stock or placeholder image sites.'
    });
  }
  if (relative.length) {
    findings.push({
      id: 'relative-images',
      level: 'error',
      title: `${plural(relative.length, 'image points', 'images point')} to a file Genesis doesn’t have`,
      items: relative.map(i => i.src),
      ask: `Replace these image paths with real URLs I give you: ${relative.map(i => i.src).join(', ')}.`
    });
  }
  return findings;
};

export const buildFixRequest = (findings: Finding[]): string => {
  const asks = unique(findings.filter(f => f.ask).map(f => f.ask as string));
  if (!asks.length) return '';
  return [
    'Please revise the code. It goes into one section of a page in our CMS, so:',
    ...asks.map(a => `- ${a}`),
    'Reply with the full updated code as three blocks: ```html, ```css and ```js.'
  ].join('\n');
};

export type WidgetCodeCheck = { findings: Finding[]; fixRequest: string; images: ImageRef[]; cssReport: ScopeCssReport };

/**
 * Checks widget code as it is now (no paste involved), for the panel's checks.
 * `parsed` adds what only the original paste knew, like removed CDN scripts.
 */
export const checkWidgetCode = (
  { html, css, js }: { html: string; css: string; js: string },
  scopeClass: string,
  parsed?: PastedCode,
  houseStyle?: HouseStyle
): WidgetCodeCheck => {
  const { report } = scopeCss(css, scopeClass);
  const images = findImages(html, css);
  const findings: Finding[] = [...scriptFindings(js, parsed), ...cssFindings(report), ...imageFindings(images)];
  const remaining = moveInlineHandlers(html);
  if (remaining.events.length) {
    findings.push({
      id: 'inline-handlers',
      level: 'warn',
      title: `${plural(remaining.events.length, 'inline event handler', 'inline event handlers')} (${unique(remaining.events).map(e => `on${e}`).join(', ')}) will be removed on published pages`,
      detail: 'Move them into the script so they keep working.'
    });
  }
  if (remaining.templated) {
    findings.push({
      id: 'templated-handlers',
      level: 'warn',
      title: `${plural(remaining.templated, 'inline handler uses', 'inline handlers use')} template values and will be removed when published`,
      ask: 'Don’t use inline onclick attributes; add event listeners in the js block and read values from data attributes.'
    });
  }
  // Keyframes get a per-section name on the page; a style attribute still naming the old one finds nothing.
  const keyframeNames = Object.keys(report.renamedKeyframes);
  if (keyframeNames.length) {
    const inline: string[] = [];
    (html.match(/\sstyle\s*=\s*(["'])[\s\S]*?\1/gi) || [])
      .filter(attr => /animation(-name)?\s*:/i.test(attr))
      .forEach((attr) => {
        keyframeNames.forEach((name) => {
          if (new RegExp(`[:\\s,]${name.replace(/[^\w-]/g, '')}(?=[\\s;,"']|$)`).test(attr)) inline.push(name);
        });
      });
    if (inline.length) {
      findings.push({
        id: 'inline-animation',
        level: 'warn',
        title: `An inline style uses the animation ${unique(inline).join(', ')}, which won’t run on the page`,
        detail: 'Keyframes are renamed for each section, so only animations set in the CSS find them.',
        ask: 'Set animations in the css block, not in style attributes.',
        items: unique(inline)
      });
    }
  }
  if (!html.trim()) {
    findings.push({ id: 'no-html', level: 'error', title: 'No HTML found', ask: 'Include the HTML for the section in an html block.' });
  }
  const size = html.length + css.length + js.length;
  if (size > 250 * 1024) {
    findings.push({ id: 'size', level: 'warn', title: `Large section (${Math.round(size / 1024)} KB)`, detail: 'Big sections slow down every page that uses them.' });
  }
  if (houseStyle) findings.push(...brandFindings(css, houseStyle));
  return { findings, fixRequest: buildFixRequest(findings), images, cssReport: report };
};

export const normalizeWidgetCode = (raw: string, scopeClass: string, houseStyle?: HouseStyle): NormalizedWidgetCode => {
  const parsed = parsePaste(raw);
  const fixes: string[] = [];

  if (parsed.removed.pageWrapper) fixes.push('Removed the page wrapper (doctype, html, head and body).');
  if (parsed.source === 'blocks' && parsed.hadProse) fixes.push('Took the code out of the reply and ignored the text around it.');
  const moved: string[] = [];
  if (parsed.removed.styleBlocks) moved.push(plural(parsed.removed.styleBlocks, 'style block', 'style blocks'));
  if (parsed.removed.inlineScripts) moved.push(plural(parsed.removed.inlineScripts, 'script', 'scripts'));
  if (moved.length) fixes.push(`Moved ${moved.join(' and ')} out of the HTML into CSS and JavaScript.`);
  if (parsed.fontImports.length) fixes.push('Kept the Google Fonts link as a CSS @import.');
  if (parsed.removed.jsonScripts) fixes.push('Removed structured data (JSON-LD) scripts, which don’t belong in a section.');
  if (parsed.removed.otherScripts) fixes.push(`Left out ${plural(parsed.removed.otherScripts, 'script block that isn’t JavaScript', 'script blocks that aren’t JavaScript')} (such as type="text/template").`);
  if (parsed.skippedLanguages.length) fixes.push(`Left out the ${parsed.skippedLanguages.join(', ')} ${parsed.skippedLanguages.length === 1 ? 'block' : 'blocks'}: commands and data aren’t part of the section.`);
  if (parsed.removed.bodyHandlers.length) fixes.push(`Moved the page’s ${unique(parsed.removed.bodyHandlers).join(', ')} into the script.`);

  const handlers = moveInlineHandlers(parsed.html);
  let js = parsed.js;
  if (handlers.events.length) {
    js = [js, handlers.js].filter(Boolean).join('\n\n');
    fixes.push(`Moved ${plural(handlers.events.length, 'inline event handler', 'inline event handlers')} (${unique(handlers.events).map(e => `on${e}`).join(', ')}) into the script, because published pages remove them.`);
  }

  const { report } = scopeCss(parsed.css, scopeClass);
  if (report.rootRules) fixes.push(':root variables and html/body styles now apply to this section.');
  if (report.droppedPageDeclarations.length) fixes.push(`Left out page-only styles from html/body: ${unique(report.droppedPageDeclarations).join('; ')}.`);
  const renamed = Object.keys(report.renamedKeyframes);
  if (renamed.length) {
    fixes.push(renamed.length === 1
      ? `Gave the animation ${renamed[0]} its own name so other sections can’t override it.`
      : `Gave ${renamed.length} animations (${renamed.join(', ')}) their own names so other sections can’t override them.`);
  }
  if (js.trim()) fixes.push('The script starts once the section is on the page, with root set to it; its element lookups search this section first.');

  const check = checkWidgetCode({ html: handlers.html, css: parsed.css, js }, scopeClass, parsed, houseStyle);
  const findings: Finding[] = [...check.findings];
  if (parsed.unsupportedLanguages.length) {
    findings.push({
      id: 'unsupported-language',
      level: 'error',
      title: `Contains ${parsed.unsupportedLanguages.join(', ')} code, which browsers can’t run as written`,
      ask: 'Use plain HTML, CSS and JavaScript only (no SCSS, TypeScript or JSX).'
    });
  }
  if (parsed.removed.bodyAttributes.length) {
    findings.push({
      id: 'body-attributes',
      level: 'info',
      title: 'The page’s body attributes were dropped',
      items: parsed.removed.bodyAttributes
    });
  }

  return {
    html: handlers.html,
    css: parsed.css,
    js,
    scoper: SCOPER_V2,
    fixes,
    findings,
    fixRequest: buildFixRequest(findings),
    images: check.images,
    source: parsed.source,
    cssReport: report
  };
};

const IMAGE_FINDINGS = ['base64-images', 'outside-images', 'relative-images'];

/**
 * A proposal after some of its images were moved to Assets: new code, image
 * findings recomputed from it (other findings kept, since some depend on the
 * original paste), and a line saying what moved.
 */
export const withMovedImages = (
  proposal: NormalizedWidgetCode,
  code: { html: string; css: string },
  movedCount: number,
  scopeClass: string
): NormalizedWidgetCode => {
  const check = checkWidgetCode({ html: code.html, css: code.css, js: proposal.js }, scopeClass);
  const findings = [
    ...proposal.findings.filter(f => !IMAGE_FINDINGS.includes(f.id)),
    ...check.findings.filter(f => IMAGE_FINDINGS.includes(f.id))
  ];
  return {
    ...proposal,
    html: code.html,
    css: code.css,
    findings,
    fixRequest: buildFixRequest(findings),
    images: check.images,
    fixes: movedCount ? [...proposal.fixes, `Moved ${plural(movedCount, 'image', 'images')} to Assets.`] : proposal.fixes
  };
};

const BRAND_FINDINGS: Record<BrandFixKind, string> = { colors: 'brand-colors', fonts: 'brand-fonts', radii: 'brand-radii' };
const BRAND_FIX_TEXT: Record<BrandFixKind, string> = {
  colors: 'Switched colors to the house color tokens.',
  fonts: 'Switched fonts to the house fonts.',
  radii: 'Switched corner radii to the house scale.'
};

/** A proposal after one kind of brand fix: new CSS and brand findings recomputed from it. */
export const withBrandFixes = (proposal: NormalizedWidgetCode, kind: BrandFixKind, houseStyle: HouseStyle): NormalizedWidgetCode => {
  const css = applyBrandFixes(proposal.css, houseStyle, [kind]);
  if (css === proposal.css) return proposal;
  const brandIds = Object.values(BRAND_FINDINGS).concat('brand-off-palette');
  const findings = [
    ...proposal.findings.filter(f => !brandIds.includes(f.id)),
    ...brandFindings(css, houseStyle)
  ];
  return { ...proposal, css, findings, fixRequest: buildFixRequest(findings), fixes: [...proposal.fixes, BRAND_FIX_TEXT[kind]] };
};
